/* eslint-disable multiline-ternary */
/* eslint-disable indent */
import React, { useEffect, useState, useContext } from 'react'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Box, Grid } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AddToPlaylist, Container, List, PageContainer, Pagination, GoogleAd, LegacyGoogleAd } from '../../components'
import { useAddToBookmarks } from '../../components/add-to-bookmarks'
import { useAddToPlaylist } from '../../components/add-to-playlist-dialog'
import { DesktopCampaignBanner } from '../../components/campaign/home-banner'
import { VerticalCampaignBanner } from '../../components/campaign/vertical-banner'
import { Breadcrumbs, CategoryMetaTags, Header, Topics } from '../../components/category-landing'
import { ContentCard } from '../../components/general-card'
import { FilterBar } from '../../components/search-results'
import { useSEO } from '../../components/seo-context'
import { UserGroupPolicy } from '../../components/user-group-policy'
import { ALLOW_PLAYLISTS_GROUP } from '../../components/user-group-policy/permissions'
import { categoryPath, categoryUrl, contentUrl } from '../../helpers/url'
import { useFormFactor, usePage, useGoogleAdsense, useProducts, useRedirect, useGroup } from '../../hooks'
import useCampaignList from '../../hooks/useCampaignList'
import { campaignSlice, categorySlice, searchResultsSlice } from '../../reducers'
import Error from '../error'
import NotFound from '../not-found'
import { useEventFilters, useSearchQuery } from '../search-results/hooks'
import { sorters } from '../search-results/params'
import { useStyles } from './style'
import useContentSearchedEvent from '../search-results/hooks/useContentSearchedEvent'
// PENG-2099
import { ALL_FREE } from '../../services/cognito/userGroups'
import { GoogleAdContext } from '../../components/google-ad/google-ad-context'

const CategoryLanding = () => {
  // PENG-2099 - Remove if Success
  const [searchParams] = useSearchParams()
  const isAdsTest = searchParams.has('adsTest')
  const adsTestValue = searchParams.get('adsTest')
  const { isInGroup } = useGroup()
  const isFreeUser = isInGroup(ALL_FREE)
  const { sendAdsRequest, adsRequestSent } = useContext(GoogleAdContext)

  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isMobile, isLargeTablet } = useFormFactor()
  const { hideLeftBanner, listColumns } = useCampaignList()
  const { locationId, '*': slug } = useParams()

  const category = useSelector(categorySlice.selectors.selectCategory)
  const children = useSelector(categorySlice.selectors.selectChildren)
  const path = useSelector(categorySlice.selectors.selectPath)
  const campaign = useSelector(campaignSlice.selectors.campaignData)

  const [error, setError] = useState()

  const [getCategory, getSearchResults] = useEvent([
    {
      eventName: 'categoryGetInfo',
      onStart: () => setError(),
      onSuccess: payload =>
        dispatch(categorySlice.actions.setCategory(payload)),
      onError: error => setError(error)
    },
    searchResultsSlice.eventBus.getSearchResults
  ])

  const { redirect } = useSEO(
    getCategory.hasBeenTriggered && !getCategory.isWorking &&
    getSearchResults.hasBeenTriggered && !getSearchResults.isWorking
  )

  useEventsOnViewLoad(() => {
    if (locationId) getCategory.trigger({ sk: locationId })
  }, [getCategory, locationId])

  useEffect(() => {
    if (locationId && category && !slug) {
      navigate(categoryUrl(category, path), { replace: true })
      redirect(`Location: ${origin}${categoryUrl(category, path)}`)
    }
  }, [locationId, slug, redirect, navigate, category, path])

  const { getEventFilters } = useEventFilters()

  const { params, filters, clearFilters, addFilter } = useSearchQuery()

  const results = useSelector(searchResultsSlice.selectors.selectResults)
  const total = useSelector(searchResultsSlice.selectors.selectTotal)
  const counts = useSelector(searchResultsSlice.selectors.selectCounts)

  const { isBookmarked, handleBookmark, bookmarking } = useAddToBookmarks(
    results
  )

  const {
    pageStart,
    pageSize,
    pageCurrent,
    pageCount,
    calcPageStart
  } = usePage(params, total)

  useEffect(() => {
    if (pageStart < 1 || !pageSize) return

    const args = {
      filters: {
        categories: [locationId],
        ...getEventFilters(filters),
        sort: 'createdAt:desc'
      },
      size: pageSize,
      from: pageStart - 1
    }
    getSearchResults.trigger(args)
    return () => dispatch(searchResultsSlice.actions.reset())
  }, [
    getSearchResults,
    filters,
    pageStart,
    pageSize,
    getEventFilters,
    locationId,
    dispatch
  ])

  useContentSearchedEvent({
    event: getSearchResults,
    filters,
    from: 'category-landing',
    category
  })

  const { showAddToPlaylist, getPlaylistArticle } = useAddToPlaylist()

  const getListItems = () =>
    getSearchResults.isWorking ? Array(pageSize).fill({}) : results

  const handlePageChange = (evt, newPage) =>
    navigate(addFilter({ start: calcPageStart(newPage) }))

  const handleCardSelect = item => navigate(contentUrl(item, 'category'))

  const handleFilterApply = ({ name, value }) =>
    navigate(addFilter({ [name]: value }))

  const handleFilterClearAll = () => navigate(clearFilters())

  const handleCategoryChange = category => {
    if (category) {
      dispatch(
        categorySlice.actions.setCategory({
          category,
          childrenCategories: [],
          pathToRoot: categoryPath(category, path)
        }))
    }
  }

  const { useAdsense } = useGoogleAdsense()

  if (error) {
    return error.statusCode === 404 ? <NotFound /> : <Error />
  }

  if (results.length > 0 && isFreeUser && !adsRequestSent && isAdsTest) {
    sendAdsRequest()
  }

  return (
    <PageContainer>
      {category && <CategoryMetaTags category={category} />}
      <UserGroupPolicy
        accessGroup={ALLOW_PLAYLISTS_GROUP}
        componentAllow={
          <AddToPlaylist
            article={getPlaylistArticle()}
            backText='Back to Category'
          />
        }
      />
      <Container maxContentWidth={1200} inset={3}>
        <Breadcrumbs
          path={path}
          onClick={handleCategoryChange}
          className={classes.breadcrumbs}
          loading={getCategory.isWorking}
        />
        <Header category={category} loading={getCategory.isWorking} />
        {(getCategory.isWorking || children?.length > 0) && (
          <Topics
            category={category}
            topics={children}
            path={path}
            onClick={handleCategoryChange}
            loading={getCategory.isWorking}
            className={classes.topics}
          />
        )}
        <Grid container>
          {!hideLeftBanner && (
            <Grid item xs={4} sm={isLargeTablet ? 3 : 4} lg={3}>
              <VerticalCampaignBanner campaign={campaign} />
              {useAdsense && (
                <fragment>
                  <br />
                  {isAdsTest
                    ? <GoogleAd
                        adKey='category-landing'
                        adPosition='left-side-bar'
                        adSource='pages>category-landing>index.js'
                        isSidePanelAd
                        adSolution={adsTestValue}
                      />
                    : <LegacyGoogleAd
                        height='250px'
                        width='280px'
                        location='category-landing>index.js'
                        isSidePanelAd
                      />}
                </fragment>
              )}
            </Grid>
          )}
          <Grid item xs>
            <FilterBar
              counts={counts}
              sorters={sorters}
              values={filters}
              onApply={handleFilterApply}
              onClearAll={handleFilterClearAll}
              classes={{ root: classes.filterBar }}
            />
            <List
              results={getListItems()}
              columns={listColumns}
              renderItem={item => (
                <ContentCard
                  content={!getSearchResults.isWorking && item}
                  onSelect={() => handleCardSelect(item)}
                  onBookmark={() => handleBookmark(item)}
                  bookmarked={isBookmarked(item)}
                  actionLoading={bookmarking(item)}
                  onAddToPlaylist={showAddToPlaylist}
                />
              )}
            />
          </Grid>
        </Grid>
        <Pagination
          page={pageCurrent}
          count={pageCount}
          size={pageSize}
          onChange={handlePageChange}
          className={classes.pagination}
        />
        {hideLeftBanner && (
          <Box mt={3} display='flex' flex={1} justifyContent='center' alignItems='center'>
            {!isMobile ? <DesktopCampaignBanner campaign={campaign} /> : <VerticalCampaignBanner campaign={campaign} />}
          </Box>
        )}
      </Container>
    </PageContainer>
  )
}

const Page = () => {
  const { hasContentHubAccess } = useProducts()
  const { standaloneRedirect } = useRedirect()

  useEffect(() => {
    if (!hasContentHubAccess) standaloneRedirect()
  }, [hasContentHubAccess, standaloneRedirect])

  if (!hasContentHubAccess) return null

  return <CategoryLanding />
}

export default React.memo(Page)
